/**
 * emotion-avatar.ts — Kili Sentient: Animated SVG Avatar with Emotion Morphing
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Replaces the static HDR_FACE_SVG in dom.ts with an emotion-reactive
 * animated SVG rendered into the #ai-avatar-3d container.
 *
 * Emotion states drive:
 *  • Eye shape morphing (via SVG <path> animation)
 *  • Sigil colour transitions (var(--sq-cyan/gold/jade/rose))
 *  • Ring rotation speed changes
 *  • Glow aura colour
 *
 * Cognitive states during tool calls drive:
 *  • Pulse speed (fast = searching, slow = recalling)
 *  • Sigil animation variant (swirl = drafting, scan = searching)
 *
 * Note: When the 3D avatar is active (always), initEmotionAvatar() is not
 * called — the 3D canvas owns #ai-avatar-3d. resetIdleTimer() drives the
 * 3D cognitive-state machine directly via registered callbacks.
 *
 * LAZY-LOAD REFACTOR:
 *   Previously this module held static imports of `resetIdleHint` and
 *   `updateAvatarCognitiveState` from `./avatar-3d`, which forced Three.js +
 *   Loom3 into the main bundle even though this module is also dynamically
 *   imported from handlers/views.ts.
 *
 *   Fix: both imports removed.  main.ts calls `registerAvatarIdleCallbacks`
 *   after the avatar module has been lazy-loaded, supplying the real functions.
 *   Until then, idle-timer callbacks are no-ops — safe because the 3D canvas
 *   is not yet mounted when a user interaction could fire resetIdleTimer().
 */

// ── Types ─────────────────────────────────────────────────────────────────────

interface EmotionConfig {
  sigilColor: string;
  glowColor:  string;
  eyeRY:      number;
  ringSpeed:  string;
  label:      string;
}

interface CognitiveConfig {
  dashArray:   string;
  ringOpacity: number;
}

// ── Emotion → visual config ────────────────────────────────────────────────────

const EMOTION_CONFIG: Record<string, EmotionConfig> = {
  joy: {
    sigilColor: '#00E5B3',
    glowColor:  'rgba(0,229,179,0.35)',
    eyeRY:      4.5,
    ringSpeed:  '5s',
    label:      'joy',
  },
  trust: {
    sigilColor: '#C9A96E',
    glowColor:  'rgba(201,169,110,0.28)',
    eyeRY:      3.5,
    ringSpeed:  '8s',
    label:      'trust',
  },
  fear: {
    sigilColor: '#FF4D6A',
    glowColor:  'rgba(255,77,106,0.30)',
    eyeRY:      5.5,
    ringSpeed:  '3s',
    label:      'fear',
  },
  surprise: {
    sigilColor: '#00E5FF',
    glowColor:  'rgba(0,229,255,0.40)',
    eyeRY:      6.0,
    ringSpeed:  '2.5s',
    label:      'surprise',
  },
  sadness: {
    sigilColor: '#7B8FA6',
    glowColor:  'rgba(100,130,160,0.20)',
    eyeRY:      2.0,
    ringSpeed:  '14s',
    label:      'sadness',
  },
  disgust: {
    sigilColor: '#A06C3E',
    glowColor:  'rgba(160,108,62,0.22)',
    eyeRY:      2.5,
    ringSpeed:  '10s',
    label:      'disgust',
  },
  anger: {
    sigilColor: '#FF5F7A',
    glowColor:  'rgba(255,95,122,0.40)',
    eyeRY:      3.0,
    ringSpeed:  '2s',
    label:      'anger',
  },
  anticipation: {
    sigilColor: '#FFD97D',
    glowColor:  'rgba(255,217,125,0.30)',
    eyeRY:      4.0,
    ringSpeed:  '6s',
    label:      'anticipation',
  },
  // Dyads
  love:           { sigilColor: '#FF9EB5', glowColor: 'rgba(255,158,181,0.30)', eyeRY: 4.2, ringSpeed: '7s',   label: 'love' },
  optimism:       { sigilColor: '#FFEB80', glowColor: 'rgba(255,235,128,0.28)', eyeRY: 4.5, ringSpeed: '6s',   label: 'optimism' },
  awe:            { sigilColor: '#B8D0FF', glowColor: 'rgba(184,208,255,0.25)', eyeRY: 5.8, ringSpeed: '3.5s', label: 'awe' },
  remorse:        { sigilColor: '#8A7090', glowColor: 'rgba(138,112,144,0.20)', eyeRY: 2.2, ringSpeed: '12s',  label: 'remorse' },
  contempt:       { sigilColor: '#C07060', glowColor: 'rgba(192,112,96,0.22)',  eyeRY: 2.8, ringSpeed: '9s',   label: 'contempt' },
  aggressiveness: { sigilColor: '#FF7040', glowColor: 'rgba(255,112,64,0.35)', eyeRY: 3.2, ringSpeed: '2.2s', label: 'aggressiveness' },
  submission:     { sigilColor: '#90A0C0', glowColor: 'rgba(144,160,192,0.20)', eyeRY: 2.6, ringSpeed: '11s',  label: 'submission' },
  disapproval:    { sigilColor: '#B08070', glowColor: 'rgba(176,128,112,0.22)', eyeRY: 2.4, ringSpeed: '10s',  label: 'disapproval' },
  neutral: {
    sigilColor: '#00E5FF',
    glowColor:  'rgba(0,229,255,0.20)',
    eyeRY:      3.5,
    ringSpeed:  '10s',
    label:      'neutral',
  },
};

// ── Cognitive state → ring variant ─────────────────────────────────────────────

const COGNITIVE_CONFIG: Record<string, CognitiveConfig> = {
  searching:   { dashArray: '1.5 4', ringOpacity: 0.70 },
  drafting:    { dashArray: '4 2',   ringOpacity: 0.55 },
  recalling:   { dashArray: '6 3',   ringOpacity: 0.45 },
  calculating: { dashArray: '2 2',   ringOpacity: 0.80 },
  thinking:    { dashArray: '3 5',   ringOpacity: 0.60 },
  idle:        { dashArray: '2.5 4', ringOpacity: 0.42 },
};

// ── SVG template ───────────────────────────────────────────────────────────────

function buildAvatarSVG(emotion: string, cognitive: string): string {
  const ec: EmotionConfig   = EMOTION_CONFIG[emotion]   || EMOTION_CONFIG.neutral;
  const cc: CognitiveConfig = COGNITIVE_CONFIG[cognitive] || COGNITIVE_CONFIG.idle;

  return `
<svg id="kili-avatar-svg" viewBox="0 0 32 32" fill="none"
     xmlns="http://www.w3.org/2000/svg"
     style="width:32px;height:32px;overflow:visible"
     data-emotion="${ec.label}" data-cognitive="${cognitive}">

  <defs>
    <radialGradient id="av-glow-g" cx="50%" cy="50%" r="50%">
      <stop offset="0%"   stop-color="${ec.sigilColor}" stop-opacity="0.55"/>
      <stop offset="100%" stop-color="${ec.sigilColor}" stop-opacity="0"/>
    </radialGradient>
    <filter id="av-blur-sm">
      <feGaussianBlur stdDeviation="1.2"/>
    </filter>
    <filter id="av-blur-md">
      <feGaussianBlur stdDeviation="2.5"/>
    </filter>
  </defs>

  <!-- Ambient glow aura (behind everything) -->
  <circle class="av-aura"
    cx="16" cy="16" r="15"
    fill="${ec.glowColor}"
    filter="url(#av-blur-md)"
    opacity="0.6"/>

  <!-- Outer hex ring (speed = emotion arousal) -->
  <g class="av-outer-ring" style="animation-duration:${ec.ringSpeed}">
    <polygon
      points="16,3.2 27.1,9.6 27.1,22.4 16,28.8 4.9,22.4 4.9,9.6"
      stroke="${ec.sigilColor}"
      stroke-width="0.55"
      fill="none"
      opacity="${cc.ringOpacity}"
      stroke-dasharray="${cc.dashArray}"/>
  </g>

  <!-- Mid hex ring (counter-rotate) -->
  <g class="av-mid-ring" style="animation-duration:${parseFloat(ec.ringSpeed) * 1.6}s">
    <polygon
      points="25.6,16 20.8,24.3 11.2,24.3 6.4,16 11.2,7.7 20.8,7.7"
      stroke="#C9A96E"
      stroke-width="0.5"
      fill="none"
      opacity="0.28"
      stroke-dasharray="1.5 5.5"/>
  </g>

  <!-- Inner hex (core stage) -->
  <polygon
    class="av-core"
    points="16,10 21.2,13 21.2,19 16,22 10.8,19 10.8,13"
    fill="rgba(0,14,26,.92)"
    stroke="${ec.sigilColor}"
    stroke-width="0.65"
    opacity="0.90"/>

  <!-- Eyes (emotion-reactive height) -->
  <ellipse class="av-eye av-eye-l"
    cx="13.2" cy="16" rx="1.4" ry="${ec.eyeRY * 0.45}"
    fill="${ec.sigilColor}" opacity="0.88"/>
  <ellipse class="av-eye av-eye-r"
    cx="18.8" cy="16" rx="1.4" ry="${ec.eyeRY * 0.45}"
    fill="${ec.sigilColor}" opacity="0.88"/>

  <!-- Sigil — A mark (Akili) -->
  <g class="av-sigil" stroke-linecap="round" stroke-linejoin="round">
    <line x1="12.5" y1="20.3" x2="16"   y2="11.3"
          stroke="${ec.sigilColor}" stroke-width="0.95" opacity="0.95"/>
    <line x1="16"   y1="11.3" x2="19.5" y2="20.3"
          stroke="${ec.sigilColor}" stroke-width="0.95" opacity="0.95"/>
    <line x1="13.7" y1="17.4" x2="18.3" y2="17.4"
          stroke="#C9A96E" stroke-width="0.8" opacity="0.9"/>
  </g>

  <!-- Apex dot -->
  <circle cx="16" cy="11.3" r="1.1"
    fill="${ec.sigilColor}" opacity="0.90"/>

</svg>`.trim();
}

// ── Module state ───────────────────────────────────────────────────────────────

let _currentEmotion:   string           = 'neutral';
let _currentCognitive: string           = 'idle';
let _morphTimeout:     ReturnType<typeof setTimeout> | null = null;
let _nudgeShown:       boolean          = false;
let _idleTimer:        ReturnType<typeof setTimeout> | null = null;

// ── LAZY-LOAD BRIDGE — 3D avatar callbacks ─────────────────────────────────────
//
// These are populated by registerAvatarIdleCallbacks() once main.ts has
// successfully lazy-loaded ./avatar-3d.  Before that point they are null
// and all calls are silent no-ops.  This is safe because:
//   • The 3D canvas is not mounted until init() resolves.
//   • No user interaction (keydown, transcription) can reach resetIdleTimer()
//     before the canvas is visible.

let _resetIdleHint:              (() => void)              | null = null;
let _updateAvatarCognitiveState: ((state: string) => void) | null = null;

/**
 * Called by main.ts after `import('./avatar-3d')` resolves.
 * Wires the 3D avatar's spring-reset and cognitive-state functions into the
 * idle-timer logic so user input events correctly drive the 3D canvas.
 */
export function registerAvatarIdleCallbacks(cbs: {
  resetIdleHint:              () => void;
  updateAvatarCognitiveState: (state: string) => void;
}): void {
  _resetIdleHint              = cbs.resetIdleHint;
  _updateAvatarCognitiveState = cbs.updateAvatarCognitiveState;
}

/**
 * Signal user re-engagement to the 3D avatar's cognitive-state machine.
 * Call on every user input event (keydown, voice transcription, etc.).
 *
 * Since 3D is always active:
 *  • Snaps springs back to centre (resetIdleHint)
 *  • Shifts cognitive state to 'thinking'
 *  • Schedules a revert to 'idle' after 8 seconds of inactivity
 *
 * The CSS .av-idle class toggle is omitted — the 3D canvas owns the container.
 */
export function resetIdleTimer(): void {
  if (_idleTimer) clearTimeout(_idleTimer);

  // 3D path: re-centre idle spring and signal active thinking.
  // No-ops until registerAvatarIdleCallbacks() has been called.
  _resetIdleHint?.();
  _updateAvatarCognitiveState?.('thinking');

  // After 8 s of inactivity, shift the 3D avatar to idle cognitive state
  _idleTimer = setTimeout(() => {
    _updateAvatarCognitiveState?.('idle');
  }, 8000);
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Initialise: replace the static HDR_FACE_SVG in #ai-avatar-3d with the
 * animated emotion-reactive SVG.
 *
 * Not called during normal operation — the 3D avatar always owns the container.
 * Retained for unit-test scenarios and potential SVG-only fallback contexts.
 */
export function initEmotionAvatar(): void {
  const container = document.getElementById('ai-avatar-3d');
  if (!container) return;
  // Do not overwrite the 3D canvas — 3D mode owns this container entirely.
  if (container.classList.contains('av-3d-mode')) return;
  container.innerHTML = buildAvatarSVG('neutral', 'idle');
  _injectAvatarCSS();
}

/**
 * Update the avatar to reflect a new Plutchik emotion label.
 * Applies a smooth CSS transition via a morph class.
 * @param emotion   — Plutchik label or 'neutral'
 * @param animate   — whether to play the morph transition (default true)
 */
export function updateAvatarEmotion(emotion: string, animate = true): void {
  if (emotion === _currentEmotion) return;
  _currentEmotion = emotion || 'neutral';

  const container = document.getElementById('ai-avatar-3d');
  if (!container) return;

  // BUG-2 GUARD: when the 3D avatar is mounted, the container owns a WebGL
  // canvas injected by avatar-3d.ts.  Writing innerHTML here would destroy
  // that canvas and replace it with the SVG fallback, breaking all subsequent
  // Three.js renders.  The 3D avatar drives its own emotion colours via
  // updateAvatarEmotion() in avatar-3d.ts (called through the registered
  // callback in handlers/chat.ts).  The SVG path is only for 2D-only contexts.
  if (container.classList.contains('av-3d-mode')) {
    // Still update the ambient drawer theme class — safe, doesn't touch the canvas
    _applyEmotionClass(emotion);
    return;
  }

  if (animate) {
    container.classList.add('av-morphing');
    if (_morphTimeout) clearTimeout(_morphTimeout);
    _morphTimeout = setTimeout(() => container.classList.remove('av-morphing'), 600);
  }

  container.innerHTML = buildAvatarSVG(_currentEmotion, _currentCognitive);

  // Apply emotion class to the drawer for ambient theme
  _applyEmotionClass(emotion);
}

/**
 * Update the avatar cognitive state during tool calls.
 * @param state — 'thinking' | 'searching' | 'drafting' | 'recalling' | 'calculating' | 'idle'
 */
export function updateAvatarCognitiveState(state: string): void {
  _currentCognitive = state || 'idle';
  const container = document.getElementById('ai-avatar-3d');
  if (!container) return;
  container.innerHTML = buildAvatarSVG(_currentEmotion, _currentCognitive);
}

/**
 * Show a wellness nudge callout above the input (throttled to once per session).
 */
export function showWellnessNudge(): void {
  if (_nudgeShown) return;
  _nudgeShown = true;

  const inputRow = document.getElementById('ai-input-row');
  if (!inputRow) return;

  const nudge = document.createElement('div');
  nudge.className = 'kili-wellness-nudge';
  nudge.innerHTML = `
    <span class="kili-nudge-icon">💙</span>
    <span class="kili-nudge-text">Sounds like a lot to manage. Would you like to pause for a moment?</span>
    <button class="kili-nudge-dismiss" aria-label="Dismiss">✕</button>
  `;

  const dismissBtn = nudge.querySelector<HTMLButtonElement>('.kili-nudge-dismiss');
  if (dismissBtn) dismissBtn.addEventListener('click', () => nudge.remove());

  if (inputRow.parentNode) {
    inputRow.parentNode.insertBefore(nudge, inputRow);
  }

  setTimeout(() => { if (nudge.parentNode) nudge.remove(); }, 12000);
}

/**
 * Reset avatar to neutral (call on new conversation).
 */
export function resetAvatarEmotion(): void {
  _currentEmotion   = 'neutral';
  _currentCognitive = 'idle';
  _nudgeShown       = false;
  // Guard: in 3D mode the canvas is owned by avatar-3d.ts; don't overwrite it.
  const container = document.getElementById('ai-avatar-3d');
  if (!container?.classList.contains('av-3d-mode')) {
    updateAvatarEmotion('neutral', false);
  }
  _applyEmotionClass('neutral');
}

// ── Private helpers ────────────────────────────────────────────────────────────

function _applyEmotionClass(emotion: string): void {
  const drawer = document.getElementById('ai-drawer');
  if (!drawer) return;
  // Remove all existing emotion classes
  drawer.className = drawer.className.replace(/\bemotion-\S+/g, '').trim();
  if (emotion && emotion !== 'neutral') {
    drawer.classList.add(`emotion-${emotion}`);
  }
}

function _injectAvatarCSS(): void {
  if (document.getElementById('kili-avatar-css')) return;
  const style = document.createElement('style');
  style.id = 'kili-avatar-css';
  style.textContent = `
    #ai-avatar-3d { transition: opacity 0.3s ease; }
    #ai-avatar-3d.av-morphing { opacity: 0.4; }
    #ai-avatar-3d svg { transition: all 0.45s cubic-bezier(0.34,1.56,0.64,1); }

    .av-outer-ring { animation: av-spin-cw  linear infinite; transform-origin: 16px 16px; }
    .av-mid-ring   { animation: av-spin-ccw linear infinite; transform-origin: 16px 16px; }
    .av-aura       { animation: av-aura-pulse 3s ease-in-out infinite; }
    .av-sigil      { animation: av-sigil-glow 4s ease-in-out infinite; }

    @keyframes av-spin-cw  { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes av-spin-ccw { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
    @keyframes av-aura-pulse {
      0%,100% { opacity: 0.5; transform: scale(1);    }
      50%     { opacity: 0.9; transform: scale(1.05); }
    }
    @keyframes av-sigil-glow {
      0%,100% { opacity: 0.80; }
      50%     { opacity: 1.00; }
    }

    .av-eye { transition: ry 0.55s cubic-bezier(0.34,1.56,0.64,1); }
  `;
  document.head.appendChild(style);
}
